import React from "react";
import { useTranslation } from "react-i18next";

import { IDocumentVersion, getPdfDocumentMetaById } from "utils/DexieDBManager";
import { RESOURCES_KEYS } from "utils/i18n/resources";
import { formatDateToFranchDate } from "utils/utils";

import {
  Box,
  Button,
  Dialog,
  Typography,
  DialogTitle,
  DialogActions,
  DialogContent,
} from "@mui/material";
import { computeStandardDocumentInfo } from "PdfDocumentGrid/PdfDocumentsList/helpers";
import { isPwa } from "utils/pwa";
import { downloadPdfFileBinaryByIdAsync } from "PdfDocumentGrid/pdfDocuments.service";
import { useMutation } from "react-query";
import {
  fetchPdfThumbnailThumbnail,
  updatePdfDocumentThumbnailBuffer,
} from "service-worker/services";
import { getAuthorizationHeader } from "utils/fetchWrapper/fetchWrapper";

const styledPdfCardDetailColumn = {
  mb: 2,
};



interface IAcknowledgmentModalProps {
  documentVersion: IDocumentVersion;
  isPdfDocumentCardDetailsOpen: boolean;
  setIsPdfDocumentCardDetailsOpen: (state: boolean) => void;
}

const PdfDocumentCardDetails: React.FunctionComponent<
  IAcknowledgmentModalProps
> = ({
  documentVersion,
  isPdfDocumentCardDetailsOpen,
  setIsPdfDocumentCardDetailsOpen,
}) => {
    const { t: translate } = useTranslation();
    const {
      parentDocument,
      uds: { ackDate },
      startDate,
      endDate,
      version,
      title,
    } = documentVersion;

    getPdfDocumentMetaById(documentVersion.file.id)


    const handleClickCancel = () => {
      setIsPdfDocumentCardDetailsOpen(false);
    };

    const handleClosePdfDocumentCardDetails = (_: any, reason: string) => {
      if (!["escapeKeyDown", "backdropClick"].includes(reason)) {
        setIsPdfDocumentCardDetailsOpen(false);
      }
    };

    // Permet de forcer le téléchargement du fichier pdf et du thumbnail lors du clic sur le bouton recharger le document
    
    const {
      isLoading: isDownloadPdfFileLoading,
      mutate: downloadPdfFileBinaryById,
    } = useMutation(
      async () => {
        try {
          const result = await downloadPdfFileBinaryByIdAsync(
            documentVersion.file,
            documentVersion.parentDocument.offline
          );
          return result;
        } catch (error) {
          console.error("Error while downloading PDF:", error);
          return null;
        }
      },
      {
        onSuccess: handleDownloadSuccess,
      }
    );

    async function handleDownloadSuccess() {

      try {
        const origin = process.env.REACT_APP_BACK_END_API;
        const authorizationHeader = { Authorization: await getAuthorizationHeader() };
        const fetchOptions = { headers: authorizationHeader };

        const thumbnail = await fetchPdfThumbnailThumbnail(
          documentVersion.file.id,
          origin!,
          fetchOptions
        );

        const fileBinary = await thumbnail.arrayBuffer();
        const pdfMeta = await getPdfDocumentMetaById(documentVersion.file.id)
        updatePdfDocumentThumbnailBuffer(pdfMeta!, fileBinary);

      } catch (error) {
        console.error("Error while handling download success:", error);
      }
    }

    const handleDocReload = () => {
      downloadPdfFileBinaryById();
    };

    return (
      <Dialog
        sx={{ width: "100%" }}
        open={isPdfDocumentCardDetailsOpen}
        onClose={handleClosePdfDocumentCardDetails}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {translate(RESOURCES_KEYS.DOCUMENT_DETAILS)}
        </DialogTitle>
        <DialogContent>
          <>
            {parentDocument.activityCode && (
              <Box sx={styledPdfCardDetailColumn} style={{ paddingTop: "10px" }}>
                <Typography variant="body2">
                  {translate(
                    RESOURCES_KEYS.CODE_ACTIVITY_AND_SERIAL_NUM_AND_LETTER
                  )}
                </Typography>
                <Typography variant="body2">
                  <strong>{computeStandardDocumentInfo(parentDocument)}</strong>
                </Typography>
              </Box>
            )}
            <Box sx={styledPdfCardDetailColumn}>
              <Typography variant="body2">
                {translate(RESOURCES_KEYS.FULL_TITLE)}
              </Typography>
              <Typography variant="body2">
                <strong>{title}</strong>
              </Typography>
            </Box>
            <Box sx={styledPdfCardDetailColumn}>
              <Typography variant="body2">
                {translate(RESOURCES_KEYS.VERSION)}
              </Typography>
              <Typography variant="body2">
                <strong>{version}</strong>
              </Typography>
            </Box>
            <Box sx={styledPdfCardDetailColumn}>
              <Typography variant="body2">
                {translate(RESOURCES_KEYS.START_DATE)}
              </Typography>
              <Typography variant="body2">
                <strong>{formatDateToFranchDate(startDate)}</strong>
              </Typography>
            </Box>
            {endDate && (
              <Box sx={styledPdfCardDetailColumn}>
                <Typography variant="body2">
                  {translate(RESOURCES_KEYS.END_DATE)}
                </Typography>
                <Typography variant="body2">
                  <strong>{formatDateToFranchDate(endDate)}</strong>
                </Typography>
              </Box>
            )}
            {ackDate && (
              <Box sx={styledPdfCardDetailColumn}>
                <Typography variant="body2">
                  {translate(RESOURCES_KEYS.ACK_DATE)}
                </Typography>
                <Typography variant="body2">
                  <strong>{formatDateToFranchDate(ackDate)}</strong>
                </Typography>
              </Box>
            )}
          </>
        </DialogContent>
        <DialogActions style={isPwa ? { justifyContent: "center" } : {}}>
          {isPwa && (
            <Button onClick={handleDocReload}>
              {translate(RESOURCES_KEYS.RELOAD_DOC)}
            </Button>
          )}
          <Button
            variant="outlined"
            onClick={handleClickCancel}
            disabled={isDownloadPdfFileLoading}
          >
            {translate(RESOURCES_KEYS.CLOSE_VIEWER_MODAL)}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

export default PdfDocumentCardDetails;
